import React, { useCallback, useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import { Link as RouterLink, useParams } from "react-router-dom";
import { useIntl } from "react-intl";
import { useSnackbar } from "notistack";
import UserContext from "context/UserContext";
import RevaText from "components/common/RevaText";
import EmptyPage from "components/common/EmptyPage";
import performRequest from "network/perform-request";
import {
  leagueCustomerTeamPlayerRoute,
  leagueCustomerTeamRoute,
} from "network/api-routes";
import { GESTION_ROUTES } from "constants/gestion-routes";
import { useGestionCustomer } from "hooks/useGestionCustomer";
import GestionTable from "components/pages/Gestion/GestionTable";
import FinanceOnly from "components/pages/Gestion/FinanceOnly";
import GestionTeamPlayerPicker from "components/pages/Gestion/GestionTeamPlayerPicker";

const memberLabel = (member) => {
  if (!member) return "";
  const name = [member.first_name, member.last_name].filter(Boolean).join(" ");
  if (member.document_number) {
    return `${name} (${member.document_number})`;
  }
  return name;
};

const playerMember = (player) => player.playerable || player.roster_member;

const GestionTeamDetail = () => {
  const { teamId } = useParams();
  const { customerId, customerName } = useGestionCustomer();
  const { user } = useContext(UserContext);
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();

  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);
  const [assignOpen, setAssignOpen] = useState(false);

  const handleError = useCallback(
    (error) => {
      enqueueSnackbar(error?.response?.data?.message || intl.formatMessage({ id: "gestion.error.generic" }), {
        variant: "error",
      });
    },
    [enqueueSnackbar, intl],
  );

  const loadTeam = useCallback(() => {
    if (!customerId || !teamId) return;
    setLoading(true);
    performRequest("GET", leagueCustomerTeamRoute(customerId, teamId), {}, true, user)
      .then((response) => setTeam(response.data))
      .catch(handleError)
      .finally(() => setLoading(false));
  }, [customerId, handleError, teamId, user]);

  useEffect(() => {
    loadTeam();
  }, [loadTeam]);

  const handleAssigned = () => {
    setAssignOpen(false);
    loadTeam();
  };

  const handleRemovePlayer = (player) => {
    if (!customerId || !teamId) return;
    if (!window.confirm(intl.formatMessage({ id: "gestion.teamDetail.confirmRemovePlayer" }))) return;
    performRequest("DELETE", leagueCustomerTeamPlayerRoute(customerId, teamId, player.id), {}, true, user)
      .then(() => {
        enqueueSnackbar(intl.formatMessage({ id: "gestion.teamDetail.playerRemoved" }), { variant: "success" });
        loadTeam();
      })
      .catch(handleError);
  };

  const handleShirtChange = (player, value) => {
    if (!customerId || !teamId) return;
    const parsed = value === "" ? null : Number(value);
    performRequest(
      "PATCH",
      leagueCustomerTeamPlayerRoute(customerId, teamId, player.id),
      { shirt_number: parsed },
      true,
      user,
    )
      .then(() => loadTeam())
      .catch(handleError);
  };

  if (!customerId || !teamId) return null;

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" sx={{ py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!team) {
    return <EmptyPage text={intl.formatMessage({ id: "gestion.teamDetail.notFound" })} />;
  }

  const players = team.players ?? [];

  return (
    <Box sx={{ width: "100%", maxWidth: "lg", mx: "auto", p: { xs: 3, md: 4 } }}>
      <Stack spacing={3}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <IconButton component={RouterLink} to={GESTION_ROUTES.teams(customerId)} size="small">
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <RevaText variant="h4">{team.name}</RevaText>
            <RevaText variant="body2" color="text.secondary">
              {team.short_name || "—"} · {customerName}
            </RevaText>
          </Box>
          <FinanceOnly>
            <Button component={RouterLink} to={GESTION_ROUTES.teamFinancials(customerId, team.id)} variant="contained">
              {intl.formatMessage({ id: "gestion.players.financials" })}
            </Button>
          </FinanceOnly>
          <Button component={RouterLink} to={GESTION_ROUTES.teams(customerId)} variant="outlined">
            {intl.formatMessage({ id: "gestion.teamDetail.backToTeams" })}
          </Button>
        </Stack>

        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} spacing={2}>
          <RevaText variant="h6">{intl.formatMessage({ id: "gestion.teamDetail.roster" })}</RevaText>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAssignOpen(true)}>
            {intl.formatMessage({ id: "gestion.teamDetail.assignPlayer" })}
          </Button>
        </Stack>

        {players.length === 0 ? (
          <EmptyPage text={intl.formatMessage({ id: "gestion.teamDetail.emptyRoster" })} />
        ) : (
          <GestionTable
            headers={[
              {
                key: "shirt_number",
                title: intl.formatMessage({ id: "gestion.teamDetail.shirtNumber" }),
                width: 100,
                renderCell: (_value, player) => (
                  <TextField
                    size="small"
                    type="number"
                    value={player.shirt_number ?? ""}
                    onChange={(event) => handleShirtChange(player, event.target.value)}
                    inputProps={{ min: 0, max: 999 }}
                    sx={{ width: 72 }}
                  />
                ),
              },
              {
                key: "name",
                title: intl.formatMessage({ id: "gestion.players.name" }),
                renderCell: (_value, player) => {
                  const member = playerMember(player);
                  return member ? memberLabel(member) : player.name;
                },
              },
              {
                key: "document",
                title: intl.formatMessage({ id: "gestion.players.document" }),
                renderCell: (_value, player) => playerMember(player)?.document_number || "—",
              },
              {
                key: "actions",
                title: intl.formatMessage({ id: "gestion.actions" }),
                align: "right",
                renderCell: (_value, player) => (
                  <Tooltip title={intl.formatMessage({ id: "gestion.teamDetail.removePlayer" })}>
                    <IconButton size="small" onClick={() => handleRemovePlayer(player)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                ),
              },
            ]}
            rows={players}
          />
        )}
      </Stack>

      <GestionTeamPlayerPicker
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        customerId={customerId}
        team={team}
        user={user}
        onAssigned={handleAssigned}
      />
    </Box>
  );
};

export default GestionTeamDetail;
